import React from 'react'
import {observer} from 'mobx-react'
import classNames from 'classnames'
import {useBasketStore, useCustomerStore, useOrderStore, useRapidDeliveryStore} from '../../store/hooks/useStore'
import {Box, Container, Flex, Stack, Text} from '../../vanilla'
import {Spinner} from '../spinner'
import {NoSelectedDelivery} from './no-selected-delivery'
import {Slot} from '../cms/Slot'
import {SlotContentItem} from '../../types/cms'
import useDeliveryBooking from '../../hooks/use-delivery-booking'
import LoadingOverlay from '../loading-overlay'
import {DeliveryDateTime} from './delivery-date-time'
import {ReservationExpiryMessage, ReservationStatus} from './reservation-status'
import {DeliveryAddress} from './delivery-address'
import {
  bookDeliverySectionContainer,
  homepageDeliveryShell,
  reservationStatusContainerError,
} from './styles.css'
import { DeliveryOrderInfo } from './delivery-order-info'
import { useSlotExpiration } from '../../store/hooks/useSlotExpiration'
import SlotsErrorMessaging from '../../pages/book-delivery/partials/booking-slots/error-messaging'
import { isMobile } from '../../utils/utils';

interface BookDeliveryProps {
  deliveryBanner?: SlotContentItem
}

export const BookDeliveryContainer = ({deliveryBanner}: BookDeliveryProps) => {
  const {isGuest} = useCustomerStore()
  const {
    basket,
    currentDeliverySlotId,
    isAppliedPostCodeDeliverable,
    processingAppliedPostCode,
    postcodeError,
    setPostcodeError
  } = useBasketStore()
  const {isReservationExpired} = useSlotExpiration()
  const {recentOrder} = useOrderStore()
  const {clearPostalCode, reserveSlot, loading} = useDeliveryBooking()
  const {isRapidDeliveryBooked} = useRapidDeliveryStore()

  const failedDeliverableStatus = basket?.c_deliveryPostalCode &&
    isAppliedPostCodeDeliverable === false && !processingAppliedPostCode

    return (
      <Box backgroundColor="white" className={homepageDeliveryShell}>
        {deliveryBanner && !isMobile() && <Slot data={deliveryBanner} /> }
        <Container
          paddingX="0px"
          paddingY={!basket && !isGuest ? '0px' : ['16px', '24px']}
          position="relative"
          className={classNames(postcodeError && reservationStatusContainerError, bookDeliverySectionContainer)}
          width="full"
        >
        <LoadingOverlay
          containerStyles={{backgroundColor: 'white'}}
          isLoading={loading || (processingAppliedPostCode && !isReservationExpired())}
        />
        {/* If there is an error with the delivery service, render the error message */}
        {postcodeError ? (
          <SlotsErrorMessaging variant='postcodeChecker' buttonAction={() => { setPostcodeError?.(false) }} />
        )
        : !basket && !isGuest ? (
          <Flex justify="center" align="center" minHeight="96px" width="full">
            <Spinner color="primary" />
          </Flex>
        ) : recentOrder ? 
          <Flex direction={["column", "row"]} gap="16px" alignItems="center">
            <DeliveryOrderInfo order={recentOrder} />
          </Flex>
        : (
          <Stack
            flexDirection={['column', 'row']}
            justify="space-between"
            alignItems={currentDeliverySlotId ? 'center' : 'flex-start'}
            spacing={
              currentDeliverySlotId && !isReservationExpired() ? ['20px', '0px'] : ['12px', '0px']
            }
            paddingBottom={['16px', '0px']}
          >
            {currentDeliverySlotId ? (
              <>
                <Box display="block" width={['full', 'auto']}>
                  <DeliveryDateTime isHomepage />
                </Box>
                {!isReservationExpired() && !isRapidDeliveryBooked && (
                  <Box width={['full', 'auto']}>
                    <DeliveryAddress isHomepage />
                  </Box>
                )}
                {isReservationExpired() && !isRapidDeliveryBooked && isMobile() && (
                  <ReservationExpiryMessage />
                )}
                {deliveryBanner && isMobile() && <Slot data={deliveryBanner} />}
                {<ReservationStatus rebookSlot={() => reserveSlot(currentDeliverySlotId)} />}
              </>
            ) : (
              <NoSelectedDelivery deliveryBanner={deliveryBanner} />
            )}
          </Stack>
        )}
      </Container>
    </Box>
  )
}

export const BookDelivery = observer(BookDeliveryContainer)
